import "./downloads.css"
import { ReactNode } from "react";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";

/**
 * Одна страница со всеми дистрибутивами вместо трёх отдельных (/mp, /qgc, /alp),
 * спрятанных под выпадающим пунктом меню.
 *
 * Тексты остались в своих namespace'ах — переводить заново ничего не пришлось.
 * Старые адреса продолжают работать редиректом сюда: на /mp и /qgc ссылается
 * документация, а она печатается и расходится вне сайта.
 */

interface DownloadLink {
    label: string;
    href: string;
}

interface Distribution {
    /** Он же якорь: /downloads#qgc. */
    id: string;
    /** Namespace со строками — тот же, что был у отдельной страницы. */
    ns: string;
    logo: string;
    /** Абзац «на основе оригинального …» со ссылкой на исходный проект. */
    originHref?: string;
    /** Список возможностей: ключ заголовка и ключи пунктов. */
    features?: { titleKey: string; itemKeys: string[] };
    downloads: DownloadLink[];
}

const DISTRIBUTIONS: readonly Distribution[] = [
    {
        id: 'mp',
        ns: 'mp',
        logo: '/image/MP.png',
        originHref: 'https://ardupilot.org/planner/',
        features: {
            titleKey: 'string_3',
            itemKeys: ['string_4', 'string_5', 'string_6', 'string_7', 'string_8']
        },
        downloads: [
            { label: 'Windows', href: 'https://ftp.air-link.space/downloads/gs/mission_planner/AirLinkMP_v2.1.exe' }
        ]
    },
    {
        id: 'qgc',
        ns: 'qgc',
        logo: '/image/QGC.png',
        originHref: 'http://qgroundcontrol.com/',
        downloads: [
            { label: 'Windows', href: 'https://disk.yandex.ru/d/JhFZsU2wXkdi1w' },
            { label: 'Ubuntu', href: 'https://disk.yandex.ru/d/luEjCNXiR2Zb6w' },
            { label: 'Android', href: 'https://disk.yandex.ru/d/ZULkofNEfF9czg' }
        ]
    },
    {
        id: 'alp',
        ns: 'alp',
        logo: '/image/ALP.png',
        downloads: [
            { label: 'Windows', href: 'https://disk.yandex.ru/d/lsSntR_f5Adh2g' }
        ]
    }
];

function Downloads(): ReactNode {
    const { t } = useTranslation(['mp', 'qgc', 'alp', 'base', 'nav_menu']);

    return (
        <div className='max-width text-page-padding'>
            <h1 className='heading downloads-title'>{t('downloads', { ns: 'nav_menu' })}</h1>

            {DISTRIBUTIONS.map((d) => (
                <section className='mission-download' id={d.id} key={d.id}>
                    <div className='mp-logo'>
                        <img src={d.logo} alt="" />
                    </div>
                    <div>
                        <div className='heading'>{t('header', { ns: d.ns })}</div>
                        <div className='text'>
                            <p>{t('string_1', { ns: d.ns })}</p>

                            {d.originHref &&
                                <p>
                                    {t('string_2', { ns: d.ns })}{' '}
                                    <a href={d.originHref}>{t('href_text', { ns: d.ns })}</a>
                                </p>
                            }

                            {d.features &&
                                <>
                                    <p>{t(d.features.titleKey, { ns: d.ns })}</p>
                                    <ul className='downloads-features'>
                                        {d.features.itemKeys.map((key) => (
                                            <li key={key}>{t(key, { ns: d.ns })}</li>
                                        ))}
                                    </ul>
                                </>
                            }
                        </div>
                    </div>
                    <div className='download-buttons'>
                        <div>{t('Download', { ns: 'base' })}:</div>
                        {d.downloads.map((link) => (
                            <Button
                                key={link.href}
                                variant='success'
                                href={link.href}
                                target='_blank' rel="noopener"
                            >
                                {link.label}
                            </Button>
                        ))}
                    </div>
                </section>
            ))}
        </div>
    )
}

export default Downloads;
